import { gtmSendEvent } from "@wojo/analytics";
import { useAuth } from "@wojo/auth";
import { useI18n } from "@wojo/localization";
import {
    AlertBasic,
    Button,
    Form,
    FormMaskedNumericField,
    FormTextField,
    Markdown,
} from "@wojo/ui";
import { Webform } from "@wojo/webforms";
import { useSubmitWebform } from "@wojo/webforms/src/use-submit-webform";
import type { E164Number } from "libphonenumber-js";
import React, { useState } from "react";
import { useTourPageContext } from "../../../_client/context";
import { ContactFormSubmitted } from "./ContactFormSubmitted";

type FormType = {
    firstName: string;
    lastName: string;
    email: string;
    phone: E164Number;
};

export type ContactFormProps = {
    formLabel: string;
    formName: Webform;
};

export const ContactForm: React.FC<ContactFormProps> = ({
    formLabel,
    formName,
}) => {
    const { contactConfig } = useTourPageContext();
    const { webSource } = useAuth();
    const submitWebform = useSubmitWebform();
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { global } = useI18n("global");
    const { productPage } = useI18n("productPage");
    const formFootnote = productPage.tripBuilder.contactUs.formTerms.l({
        phone: `[${contactConfig?.phoneNumberFormatted}](tel:${contactConfig?.phoneNumber})`,
    });

    return formSubmitted ? (
        <ContactFormSubmitted />
    ) : (
        <>
            <Form<FormType>
                aria-label={formLabel || undefined}
                onSubmit={async (data) => {
                    const response = await submitWebform({
                        formName,
                        department:
                            webSource === "GROUPS" ? "Group Member" : "Indies",
                        email: data.email,
                        firstName: data.firstName,
                        lastName: data.lastName,
                        phone: data.phone,
                    });
                    if (!response.ok) {
                        setError(
                            (await response.text()) ||
                                productPage.tripBuilder.contactUs.defaultFormError.l(),
                        );
                        gtmSendEvent({
                            event: "track",
                            ga4Tracking: {
                                ga4_object: "LEAD_FORM",
                                ga4_action: "SUBMISSION_FAILED",
                                leadFormId: formName,
                            },
                        });
                    } else {
                        gtmSendEvent({
                            event: "track",
                            ga4Tracking: {
                                ga4_object: "LEAD_FORM",
                                ga4_action: "SUBMITTED",
                                leadFormId: formName,
                            },
                        });
                        setFormSubmitted(true);
                    }
                }}
            >
                <div
                    style={{
                        display: "grid",
                        gap: "var(--g-spacing-sm)",
                        marginTop: "var(--g-spacing-md)",
                        marginBottom: "var(--g-spacing-lg)",
                    }}
                >
                    {!!error && <AlertBasic status="alert" title={error} />}
                    <FormTextField
                        name="firstName"
                        label={global.formFieldLabels.firstName.l()}
                        aria-required
                        validation={{
                            required:
                                global.validationErrors.firstNameRequired.l(),
                        }}
                    />
                    <FormTextField
                        name="lastName"
                        label={global.formFieldLabels.lastName.l()}
                        aria-required
                        validation={{
                            required:
                                global.validationErrors.lastNameRequired.l(),
                        }}
                    />
                    <FormTextField
                        name="email"
                        autoComplete="email"
                        label={global.formFieldLabels.email.l()}
                        id="email"
                        aria-required
                        validation={{
                            required: global.validationErrors.emailRequired.l(),
                            pattern: {
                                value: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/,
                                message:
                                    global.validationErrors.emailInvalid.l(),
                            },
                        }}
                    />
                    <FormMaskedNumericField
                        name="phone"
                        label={global.formFieldLabels.phone.l()}
                        aria-required
                        format={global.inputMasks.phone.l()}
                        descriptionText={productPage.tripBuilder.contactUs.formPhoneNote.l()}
                        validation={{
                            required: global.validationErrors.phoneRequired.l(),
                            pattern: {
                                value: /^\(\d{3}\)\s\d{3}-\d{4}$/,
                                message:
                                    global.validationErrors.phoneInvalid.l(),
                            },
                        }}
                    />
                </div>
                <Button type="submit">
                    {productPage.tripBuilder.contactUs.formSubmit.l()}
                </Button>
            </Form>
            <Markdown
                markdown={formFootnote}
                style={{ marginTop: "var(--g-spacing-md)" }}
                onClickCapture={(e) => {
                    if (e.target instanceof HTMLAnchorElement) {
                        if (e.target.href.startsWith("tel:")) {
                            gtmSendEvent({
                                event: "track",
                                ga4Tracking: {
                                    ga4_object: "TRIP_BUILDER",
                                    ga4_action: "PHONE_CALL",
                                    phoneNumber:
                                        contactConfig?.phoneNumberFormatted ||
                                        "",
                                },
                            });
                        } else {
                            gtmSendEvent({
                                event: "track",
                                ga4Tracking: {
                                    ga4_object: "TRIP_BUILDER",
                                    ga4_action: "LINK_CLICKED",
                                    clickText: e.target.textContent || "",
                                    destinationUrl: e.target.href,
                                },
                            });
                        }
                    }
                }}
            />
        </>
    );
};
